import type { IssueEntity, IssueStatus, IssuePriority } from '../../domain/entities/IssueEntity.js';
import type {
  IIssueRepository, IssueCreateInput, IssueListOptions, IssueStats, IssueStatsOptions, IssueUpdateInput,
} from '../../domain/repositories/IIssueRepository.js';
import { IssueRemoteDataSource } from '../datasources/IssueRemoteDataSource.js';
import type { IssueDto } from '../dtos/IssueDto.js';

function fullName(p: { first_name: string; last_name: string } | null | undefined): string | null {
  if (!p) return null;
  return `${p.first_name} ${p.last_name}`.trim() || null;
}

function sessionLabel(s: { id: string; notes: string | null; scheduled_start_at: string | null } | null | undefined): string | null {
  if (!s) return null;
  if (s.scheduled_start_at) {
    const d = new Date(s.scheduled_start_at);
    const timeStr = d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Ho_Chi_Minh' });
    const dateStr = d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Ho_Chi_Minh' });
    return `${timeStr} - ${dateStr}`;
  }
  return `#${s.id.substring(0, 8)}`;
}



function mapDtoToEntity(dto: IssueDto): IssueEntity {
  const assignedToIds = dto.assigned_to_ids ?? (dto.assigned_to_id ? [dto.assigned_to_id] : []);
  return {
    id: dto.id,
    orgId: dto.org_id,
    projectId: dto.project_id,
    locationId: dto.location_id ?? null,
    sessionId: dto.session_id ?? null,
    name: dto.name,
    description: dto.description ?? null,
    status: (dto.status as IssueStatus) ?? 'open',
    priority: (dto.priority as IssuePriority) ?? 'low',
    dueDate: dto.due_date ?? null,
    assignedToId: dto.assigned_to_id ?? (assignedToIds[0] || null),
    assignedToIds,
    completedAt: dto.completed_at ?? null,
    categoryId: dto.category_id ?? null,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    createdBy: dto.created_by,
    assignedToName: fullName(dto.assigned_to),
    createdByName: fullName(dto.creator),
    categoryName: dto.category?.name ?? null,
    locationName: dto.location?.name ?? null,
    sessionLabel: sessionLabel(dto.session),
    photos: dto.photos ?? [],
  };
}

export class IssueRepository implements IIssueRepository {
  constructor(private readonly remoteDataSource: IssueRemoteDataSource) {}

  async getById(id: string): Promise<IssueEntity | null> {
    const dto = await this.remoteDataSource.getIssueById(id);
    return dto ? mapDtoToEntity(dto) : null;
  }

  async list(options: IssueListOptions): Promise<{ data: IssueEntity[]; total: number }> {
    const { data, total } = await this.remoteDataSource.listIssues(options);
    return { data: data.map(mapDtoToEntity), total };
  }

  async stats(options: IssueStatsOptions): Promise<IssueStats> {
    return this.remoteDataSource.countIssues(options);
  }

  async create(input: IssueCreateInput): Promise<IssueEntity> {
    const assignedToIds = input.assignedToIds ?? (input.assignedToId ? [input.assignedToId] : []);
    const dto = await this.remoteDataSource.createIssue({
      org_id: input.orgId,
      project_id: input.projectId,
      name: input.name,
      description: input.description ?? null,
      status: input.status ?? 'open',
      priority: input.priority ?? 'low',
      due_date: input.dueDate ?? null,
      assigned_to_id: assignedToIds[0] || input.assignedToId || null,
      assigned_to_ids: assignedToIds,
      category_id: input.categoryId ?? null,
      location_id: input.locationId ?? null,
      session_id: input.sessionId ?? null,
      created_by: input.createdBy,
    });
    if (!dto) throw new Error('Issue creation failed');
    return mapDtoToEntity(dto);
  }

  async update(id: string, input: IssueUpdateInput): Promise<IssueEntity | null> {
    const payload: Partial<IssueDto> = {};
    if (input.name !== undefined) payload.name = input.name;
    if (input.description !== undefined) payload.description = input.description;
    if (input.status !== undefined) payload.status = input.status;
    if (input.priority !== undefined) payload.priority = input.priority;
    if (input.dueDate !== undefined) payload.due_date = input.dueDate;
    if (input.assignedToIds !== undefined) {
      payload.assigned_to_ids = input.assignedToIds;
      payload.assigned_to_id = input.assignedToIds[0] || null;
    } else if (input.assignedToId !== undefined) {
      payload.assigned_to_id = input.assignedToId;
      payload.assigned_to_ids = input.assignedToId ? [input.assignedToId] : [];
    }
    if (input.categoryId !== undefined) payload.category_id = input.categoryId;
    if (input.locationId !== undefined) payload.location_id = input.locationId;
    if (input.sessionId !== undefined) payload.session_id = input.sessionId;
    if (input.completedAt !== undefined) payload.completed_at = input.completedAt;
    const dto = await this.remoteDataSource.updateIssue(id, payload);
    return dto ? mapDtoToEntity(dto) : null;
  }

  async delete(id: string): Promise<void> {
    await this.remoteDataSource.deleteIssue(id);
  }
}
